import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { TableCell, TableRow } from 'semantic-ui-react';
import { Eye } from '@phosphor-icons/react';
import { BodyContainer, ColoredPill, MuteText, PageTitle } from '../../../../elements';
import { PackingDetailHook } from '../../../../hooks/packing';
import { PackingDetailHeaderLead } from '../../../../page-components/packaging';
import { AppTable, ItemImage } from '../../../../components';
import { CommonUtility, ToastMessage } from '../../../../utility';
import { MultiplePacks } from '../../../../page-components/packaging/detail/multiple-packs';
import { PackagingService } from '../../../../utility/services/packaging';
import { downloadPDF } from '../../../../page-components/packaging/detail/common';
import { useGlobalContext } from '@contexts/global';
import { useAppHeaderLead } from '../../../../hooks/useAppHeaderLead';

const TitleContainer = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

export function PickingCompletedDetailScreen() {
  const { containerNumber } = useParams();
  const [searchParams] = useSearchParams();
  const warehouseId = searchParams.get('warehouseId');
  const { currentStation, currentWarehouse } = useGlobalContext();

  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);

  const { data, loading } = PackingDetailHook(
    containerNumber,
    'containerId',
    'completed',
    warehouseId,
  );

  const key = useMemo(() => (data?.tote ? 'tote' : 'pallet'), [data]);
  const isPallet = key === 'pallet';

  useEffect(() => {
    if (!CommonUtility.isValidObject(data?.[key]) || key !== 'tote') return;
    const packages = [];
    const itemStatus = {};

    data?.[key].contents.forEach(content => {
      if (content.status === 'shipped') {
        if (itemStatus[content?.productId?._id]) {
          itemStatus[content?.productId?._id] += content.quantity || 1;
        } else {
          itemStatus[content?.productId?._id] = 1;
        }
      }
    });

    if (CommonUtility.isValidArray(data?.[key]?.shipping?.labels)) {
      data?.[key].shipping.labels.forEach(label => {
        const pack = {
          items: label.items,
          label,
        };

        pack.items = pack.items.map(item => {
          if (itemStatus[item.item_id] > 0) {
            if (item.qty >= itemStatus[item.item_id]) {
              item.qty = itemStatus[item.item_id];
            }
            itemStatus[item.item_id] -= item.qty;
          } else {
            item.qty = 0;
          }
          return item;
        });
        packages.push(pack);
      });
      if (Object.values(itemStatus).some(x => !!x)) {
        const pack = {
          items: [],
        };
        Object.keys(itemStatus).forEach(item_id => {
          if (itemStatus[item_id]) {
            pack.items.push({
              item_id,
              qty: itemStatus[item_id],
            });
          }
        });
        packages.push(pack);
      }
    } else {
      const pack = {
        items: [],
      };

      pack.items = Object.keys(itemStatus).map(item_id => ({
        item_id,
        qty: itemStatus[item_id],
      }));

      packages.push(pack);
    }

    setPackages(packages);
  }, [data, key]);

  const columns = useMemo(() => {
    const arr = [
      {
        text: 'Item',
        key: 'item',
        textAlign: 'left',
        width: 4,
      },
      {
        text: 'Quantity',
        key: 'quantity',
        textAlign: 'left',
        width: 1,
      },
      {
        text: isPallet ? 'Label' : 'Status',
        key: 'label',
        textAlign: 'left',
        width: 2,
      },
    ];
    if (isPallet) {
      arr.splice(2, 0, {
        text: 'Order',
        key: 'order',
        textAlign: 'left',
        width: 2,
      });
    }
    return arr;
  }, [isPallet]);

  const askToPrint = async (toPrint, fileName = 'document.pdf') => {
    if (toPrint) {
      if (
        currentWarehouse?.shipnodeConfigurations?.shipnode &&
        currentStation?.computerId &&
        currentStation?.printerId
      ) {
        // do shipnode printing
        try {
          const body = {
            message: new Date().toString(),
            computer_id: currentStation?.computerId,
            printer_id: currentStation?.printerId,
            url: toPrint,
          };
          await PackagingService.printLabel(body);
        } catch (error) {
          ToastMessage.apiError(error);
        }
      } else {
        downloadPDF(toPrint, fileName);
      }
    }
  };

  useEffect(() => {
    if (CommonUtility.isValidArray(data?.[key]?.contents)) {
      setItems(data?.[key]?.contents);
    }
  }, [data, key]);

  useAppHeaderLead(() => <PackingDetailHeaderLead data={data?.[key]} />, [data, key]);

  return (
    <BodyContainer>
      <div className="my-2">
        <PageTitle>Items</PageTitle>
      </div>
      {isPallet ? (
        <AppTable
          data={data?.[key]?.contents}
          columns={columns}
          RowComponent={<ItemRow isPallet={isPallet} />}
        />
      ) : (
        <MultiplePacks
          askToPrint={askToPrint}
          fileName={`${data?.[key]?.order?.marketplace_order_display_id} - ${data?.[key]?.toteNumber}`}
          hasShipNode={currentWarehouse?.shipnodeConfigurations?.shipnode}
          items={items}
          packages={packages}
          allItemsPacked
          statusToCheck="shipped"
          readOnly
        />
      )}
    </BodyContainer>
  );
}

const FileHref = styled.a`
  display: flex;
  align-items: center;
`;

function ItemRow({ item, isPallet }) {
  return (
    <TableRow>
      <TableCell>
        <div className="d-flex">
          <ItemImage
            imageKey="p_images"
            displayImageKey="display_image"
            item={item?.productId}
          />
          <div className="d-flex flex-column ml-3">
            <TitleContainer>{item?.productId?.p_title}</TitleContainer>
            <div className="d-flex">
              <MuteText>SKU</MuteText>
              <span className="ml-2">{item?.productId?.p_sku}</span>
            </div>
            <div className="d-flex">
              <MuteText>UPC</MuteText>
              <span className="ml-2">{item?.productId?.upc}</span>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>{item.quantity}</TableCell>
      {isPallet && <TableCell>{item.order?.marketplace_order_display_id}</TableCell>}
      <TableCell>
        {isPallet ? (
          <FileHref href={item.shipping?.labels?.[0]?.url} download target="_blank">
            <Eye className="mr-2" size={18} />
            <span>View Label</span>
          </FileHref>
        ) : (
          <ColoredPill>{CommonUtility.toTitleCase(item.status)}</ColoredPill>
        )}
      </TableCell>
    </TableRow>
  );
}
